import { Link } from 'react-router-dom'
import { useMyLoot } from '@/hooks/queries'
import { backgroundImage } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { UserPrize } from '@/models/prize'
import { SkeletonLine } from './ui/Skeleton'
import { KeyStrip, MASKED_KEY } from './KeyStrip'

interface KeyCardProps {
  title: string
  subtitle: string
  /** Null while the key is unavailable; the card still shows its masked state. */
  value: string | null
  art?: string | null
  href?: string | null
  featured?: boolean
}

/**
 * One won key.
 *
 * The key is the object here, so it is the largest thing on the card and set in
 * the monospace face — the one place in this product where every character
 * matters. It stays covered until asked for: loot is worth the beat of turning
 * over, and a screenful of live keys is a screenful you cannot screenshot.
 */
function KeyCard({ title, subtitle, value, art, href, featured = false }: KeyCardProps) {
  return (
    <article className={cn('key-card', featured && 'featured')}>
      <div
        className={cn('key-card-art', !art && 'placeholder')}
        style={art ? backgroundImage(art) : undefined}
        aria-hidden="true"
      >
        {!art ? (
          <svg viewBox="0 0 32 32" className="key-card-glyph" aria-hidden="true">
            <circle cx="11" cy="16" r="5.4" />
            <path d="M16.4 16H27M23 16v4.2M19.6 16v3" />
          </svg>
        ) : null}
      </div>

      <div className="key-card-body">
        <div className="key-card-heading">
          <h3 className="key-card-title">{title}</h3>
          <p className="key-card-subtitle">{subtitle}</p>
        </div>

        <KeyStrip value={value} title={title} />

        {href ? (
          <Link className="key-card-link" to={href}>
            View giveaway
          </Link>
        ) : null}
      </div>
    </article>
  )
}

/**
 * A key card's own markup while loot loads. The key strip is shown for real
 * in its covered state -- it looks the same for every key until revealed.
 */
function KeyCardSkeleton() {
  return (
    <article className="key-card is-skeleton" aria-hidden="true">
      <div className="key-card-art skeleton" />
      <div className="key-card-body">
        <div className="key-card-heading">
          <div className="key-card-title">
            <SkeletonLine width="62%" />
          </div>
          <div className="key-card-subtitle">
            <SkeletonLine width="38%" />
          </div>
        </div>
        <div className="key-strip">
          <span className="key-strip-value">{MASKED_KEY}</span>
          <span className="key-strip-hint">Reveal</span>
        </div>
      </div>
    </article>
  )
}

/** `/my/loot` — every key this account has won. */
export function MyLoot() {
  const { data: won = [], isPending } = useMyLoot()

  if (isPending) {
    return (
      <div className="loot-grid">
        {Array.from({ length: 3 }, (_, index) => (
          <KeyCardSkeleton key={index} />
        ))}
      </div>
    )
  }

  if (won.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">No keys yet</div>
        <div className="empty-state-body">
          Enter a giveaway and anything you win lands here, ready to redeem.
        </div>
      </div>
    )
  }

  return (
    <div className="loot-grid">
      {won.map((prize: UserPrize) => (
        <KeyCard
          key={prize.id ?? prize.key}
          title={prize.title}
          // The title already falls back to the giveaway when the prize has no
          // game attached, and repeating it underneath said nothing twice.
          subtitle={prize.game_info?.name ? (prize.giveaway_name ?? 'Won key') : 'Won key'}
          value={prize.key}
          art={prize.game_info?.display_cover_media || null}
          href={prize.giveaway_path}
        />
      ))}
    </div>
  )
}
